import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useSupportRecipientSuggestionsQuery } from '../../../../queries/Support/useSupportRecipientSuggestions';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { Pill } from '../../../../web/components-v2/Pill';
import { MIN_SUPPORT_SEARCH_LENGTH } from '../../../../utils/Support/supportHelpers';
import { composerStyles as styles } from './composerStyles';
import { applyRecipientCompletion, buildRecipientGroups, getRecipientFragment } from './recipientSuggestions';

const SUGGESTION_DEBOUNCE_MS = 250;

interface ComposerRecipientInputProps {
	accountName?: string;
	ariaLabel: string;
	autoFocus?: boolean;
	/** Addresses already in the composer's address fields; never offered as a suggestion. */
	excludedEmails: string[];
	onChange: (value: string) => void;
	placeholder: string;
	ticketId: string;
	value: string;
}

/**
 * A comma-separated address field with a teammate / account-user typeahead on the address the caret is
 * in. The field stays a plain string — send, draft persistence and validation all read it unchanged — so
 * picking a suggestion only rewrites that one address.
 */
export const ComposerRecipientInput: React.FC<ComposerRecipientInputProps> = ({
	accountName,
	ariaLabel,
	autoFocus,
	excludedEmails,
	onChange,
	placeholder,
	ticketId,
	value,
}) => {
	const inputRef = React.useRef<HTMLInputElement>(null);
	const menuRef = React.useRef<HTMLDivElement>(null);
	const listId = React.useId();
	const [caret, setCaret] = React.useState(value.length);
	const [isFocused, setIsFocused] = React.useState(false);
	const [isDismissed, setIsDismissed] = React.useState(false);
	const [highlightedIndex, setHighlightedIndex] = React.useState(0);
	const pendingCaretRef = React.useRef<number | null>(null);

	const fragment = getRecipientFragment(value, caret);
	const isSearchable = isFocused && !isDismissed && fragment.text.length >= MIN_SUPPORT_SEARCH_LENGTH;
	const debouncedQuery = useDebounceValue(isSearchable ? fragment.text : '', SUGGESTION_DEBOUNCE_MS);
	const suggestionsQuery = useSupportRecipientSuggestionsQuery(ticketId, debouncedQuery, isSearchable);

	const groups = isSearchable
		? buildRecipientGroups({
				accountName,
				excludedEmails,
				query: fragment.text,
				suggestions: suggestionsQuery.data,
			})
		: [];
	const options = groups.flatMap(group => group.options);
	// Rows are grouped for ARIA but navigated as one list; dedupe by email makes the address the key.
	const indexByEmail = new Map(options.map((option, index) => [option.email, index]));
	const isOpen = options.length > 0;
	const activeIndex = Math.min(highlightedIndex, options.length - 1);

	// The caret has to be restored by hand: the field is controlled, so React re-renders the completed
	// value with the caret back at the end of it.
	React.useLayoutEffect(() => {
		if (pendingCaretRef.current == null) return;
		inputRef.current?.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current);
		pendingCaretRef.current = null;
	}, [value]);

	// Arrow keys can walk the highlight past the menu's own scroll window.
	React.useEffect(() => {
		const active = menuRef.current?.querySelector('[aria-selected="true"]');
		active?.scrollIntoView?.({ block: 'nearest' });
	}, [activeIndex]);

	// Any deliberate move of the caret revives a dismissed picker, including into another address in the
	// same field — otherwise one Escape silences this field until the next character is typed.
	const syncCaret = (target: HTMLInputElement) => {
		setCaret(target.selectionStart ?? target.value.length);
		setIsDismissed(false);
	};

	const acceptSuggestion = (index: number) => {
		const option = options[index];
		if (!option) return;
		const completion = applyRecipientCompletion(value, fragment, option.email);
		setCaret(completion.caret);
		pendingCaretRef.current = completion.caret;
		setHighlightedIndex(0);
		onChange(completion.value);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (!isOpen) return;
		switch (event.key) {
			case 'ArrowDown':
			case 'ArrowUp': {
				event.preventDefault();
				const delta = event.key === 'ArrowDown' ? 1 : -1;
				setHighlightedIndex((activeIndex + delta + options.length) % options.length);
				return;
			}
			case 'Enter':
			case 'Tab': {
				event.preventDefault();
				acceptSuggestion(activeIndex);
				return;
			}
			case 'Escape': {
				// Kept off the composer's own Escape handling — this one only closes the picker.
				event.preventDefault();
				event.stopPropagation();
				setIsDismissed(true);
				return;
			}
			default:
				return;
		}
	};

	return (
		<div className={css(styles.recipientField)}>
			<Input
				ref={inputRef}
				styles={[styles.addressInput]}
				type='text'
				placeholder={placeholder}
				aria-label={ariaLabel}
				value={value}
				autoFocus={autoFocus}
				// Chrome ignores autocomplete='off' on a field its heuristics read as an email address, and
				// opens its saved-address bubble over this one's suggestions. A declared non-address type is
				// what makes it stand down; a credential type would too, but password managers act on those.
				autoComplete='one-time-code'
				spellCheck={false}
				role='combobox'
				aria-expanded={isOpen}
				aria-autocomplete='list'
				aria-controls={isOpen ? listId : undefined}
				aria-activedescendant={isOpen ? `${listId}-${activeIndex}` : undefined}
				onChange={event => {
					syncCaret(event.target);
					setHighlightedIndex(0);
					onChange(event.target.value);
				}}
				onClick={event => syncCaret(event.currentTarget)}
				onKeyUp={event => {
					// Escape's own keyup must not revive what its keydown just dismissed.
					if (event.key !== 'Escape') syncCaret(event.currentTarget);
				}}
				onKeyDown={handleKeyDown}
				onFocus={event => {
					syncCaret(event.currentTarget);
					setIsFocused(true);
				}}
				onBlur={() => setIsFocused(false)}
			/>
			{isOpen && (
				<div
					ref={menuRef}
					className={css(styles.recipientMenu)}
					id={listId}
					role='listbox'
					aria-label={`${ariaLabel} suggestions`}
				>
					{groups.map(group => (
						<div key={group.heading} role='group' aria-label={group.heading}>
							<div className={css(styles.suggestionMenuHeader)} aria-hidden='true'>
								{group.heading}
							</div>
							{group.options.map(option => {
								const index = indexByEmail.get(option.email) ?? 0;
								return (
									<Button
										key={option.email}
										id={`${listId}-${index}`}
										type='button'
										variant='ghost'
										size='sm'
										role='option'
										tabIndex={-1}
										aria-selected={index === activeIndex}
										styles={[
											styles.suggestionItem,
											styles.recipientItem,
											...(index === activeIndex ? [styles.suggestionItemActive] : []),
										]}
										// Keeps focus (and the caret) in the field, so the click lands on the address being typed.
										onMouseDown={event => event.preventDefault()}
										onClick={() => acceptSuggestion(index)}
									>
										<span className={css(styles.recipientItemName)}>
											<span className={css(styles.suggestionItemName)}>{option.label}</span>
											{option.statusLabel && (
												<Pill
													variant='label'
													color={option.statusColor ?? 'gray'}
													size='small'
													label={option.statusLabel}
												/>
											)}
										</span>
										<span className={css(styles.suggestionItemSub)}>{option.email}</span>
									</Button>
								);
							})}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
